"use client";

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Logo } from '@/components/icons';
import Link from 'next/link';
import { Newspaper, Zap, CheckCircle, Users, Quote, ShieldAlert, Clock, ThumbsDown, Check, ArrowRight, Menu } from 'lucide-react';
import Image from 'next/image';
import { Sheet, SheetTrigger, SheetContent } from '@/components/ui/sheet';

export default function LandingPage() {
  return (
    <div className="flex flex-col min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur-sm">
        <div className="container flex h-16 items-center justify-between px-4 md:px-6">
          <Link href="/" className="flex items-center gap-2 font-bold" prefetch={false}>
            <div className="flex size-8 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Logo className="size-5" />
            </div>
            <span className="font-headline text-xl">My Right Info</span>
          </Link>
          <nav className="hidden items-center gap-1 md:flex">
            <Button variant="ghost" asChild>
              <Link href="#features" prefetch={false}>Fonctionnalités</Link>
            </Button>
            <Button variant="ghost" asChild>
              <Link href="#promise" prefetch={false}>Notre Promesse</Link>
            </Button>
            <Button variant="ghost" asChild>
                <Link href="/briefing" prefetch={false}>Générateur</Link>
            </Button>
            <Button variant="outline" disabled>Connexion</Button>
          </nav>
          <div className="md:hidden flex items-center gap-2">
             <Button size="sm" asChild>
                <Link href="/briefing" prefetch={false} className="flex items-center gap-1">
                    <Zap className="size-4" />
                    Brief
                </Link>
             </Button>
            <Sheet>
                <SheetTrigger asChild>
                    <Button variant="outline" size="icon">
                        <Menu className="size-5" />
                        <span className="sr-only">Ouvrir le menu</span>
                    </Button>
                </SheetTrigger>
                <SheetContent side="right">
                    <nav className="grid gap-6 text-lg font-medium mt-12">
                        <Link href="#features" className="flex items-center gap-2 text-muted-foreground hover:text-foreground" prefetch={false}>
                            Fonctionnalités
                        </Link>
                        <Link href="#promise" className="flex items-center gap-2 text-muted-foreground hover:text-foreground" prefetch={false}>
                            Notre Promesse
                        </Link>
                         <Link href="#" className="flex items-center gap-2 text-muted-foreground hover:text-foreground" prefetch={false}>
                           Tarifs
                        </Link>
                        <Button variant="outline" disabled>Connexion</Button>
                    </nav>
                </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center">
        {/* Section A: Héros (Above the Fold) */}
        <section id="hero" className="relative w-full flex justify-center items-center text-center overflow-hidden bg-white py-20 md:py-32 lg:py-40">
           {/* Animated Background */}
           <div className="absolute inset-0 -z-10 overflow-hidden">
            <div className="absolute left-1/4 top-1/4 size-96 animate-blob rounded-full bg-primary/20 opacity-50 blur-3xl filter"></div>
            <div className="animation-delay-2000 absolute bottom-1/4 right-1/4 size-96 animate-blob rounded-full bg-accent/20 opacity-50 blur-3xl filter"></div>
          </div>
          <div className="container relative z-10 px-4 md:px-6">
            <h1 className="font-headline text-4xl font-extrabold tracking-tighter sm:text-6xl md:text-7xl lg:text-8xl text-primary">
              L'actualités en 5 minutes.
            </h1>
            <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground md:text-xl">
              L'actualité géopolitique est complexe. Votre briefing ne devrait pas l'être. Accédez à l'essentiel des faits, vérifiés et synthétisés.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4">
              <Button size="lg" asChild>
                <Link href="/briefing" prefetch={false}>
                  DÉMARRER MON BRIEFING GRATUIT
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Section C: Problèmes */}
        <section id="problems" className="w-full flex justify-center bg-background py-16 md:py-24">
          <div className="container px-4 md:px-6">
            <div className="mb-12 text-center">
              <h2 className="font-headline text-3xl font-bold tracking-tight sm:text-4xl">Le problème n'est pas l'information. C'est le bruit.</h2>
            </div>
            <div className="mx-auto grid max-w-4xl gap-8 md:grid-cols-3">
              <Card className="text-center">
                <CardHeader>
                  <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-destructive/10">
                    <Clock className="size-6 text-destructive" />
                  </div>
                  <CardTitle>Doomscrolling Infini</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">Des heures perdues à chercher l'essentiel dans un flux infini.</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardHeader>
                  <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-destructive/10">
                    <ShieldAlert className="size-6 text-destructive" />
                  </div>
                  <CardTitle>Fake News & Biais</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">La difficulté de distinguer le signal du bruit et de la manipulation.</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardHeader>
                  <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-destructive/10">
                    <ThumbsDown className="size-6 text-destructive" />
                  </div>
                  <CardTitle>Anxiété Médiatique</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">La surcharge d'informations négatives impactant votre bien-être.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Section D: Solution */}
        <section id="solution" className="w-full flex justify-center bg-white py-16 md:py-24">
          <div className="container text-center px-4 md:px-6">
             <h2 className="font-headline text-3xl font-bold tracking-tight sm:text-4xl">L'Intelligence Artificielle au service de VOTRE clarté.</h2>
             <div className="relative mt-12 flex w-full max-w-4xl mx-auto items-center justify-between">
                <div className="text-center">
                  <Newspaper className="mx-auto size-10 text-muted-foreground"/>
                  <p className="mt-2 font-semibold">Sources Multiples</p>
                </div>
                <ArrowRight className="size-12 text-primary shrink-0 mx-4"/>
                <div className="rounded-lg border-2 border-primary bg-primary/10 p-4 text-center shadow-lg">
                  <Logo className="mx-auto size-10 text-primary"/>
                  <p className="mt-2 font-bold text-primary">Filtre IA My Right Info</p>
                </div>
                <ArrowRight className="size-12 text-primary shrink-0 mx-4"/>
                <div className="text-center">
                  <CheckCircle className="mx-auto size-10 text-green-500"/>
                  <p className="mt-2 font-semibold">Briefing Pur</p>
                </div>
            </div>
          </div>
        </section>

        {/* Section E: Bénéfices */}
        <section id="features" className="w-full flex justify-center bg-background py-16 md:py-24">
            <div className="container mx-auto max-w-4xl px-4 md:px-6">
              <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                <li className="flex items-start gap-4">
                  <Check className="mt-1 size-6 shrink-0 text-green-500" />
                  <div>
                    <h3 className="text-lg font-semibold">Gagnez 1h par jour</h3>
                    <p className="text-muted-foreground">Libérez du temps précieux en allant droit à l'essentiel.</p>
                  </div>
                </li>
                 <li className="flex items-start gap-4">
                  <Check className="mt-1 size-6 shrink-0 text-green-500" />
                  <div>
                    <h3 className="text-lg font-semibold">Comprenez les enjeux réels</h3>
                    <p className="text-muted-foreground">Une vision claire des événements qui comptent vraiment.</p>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <Check className="mt-1 size-6 shrink-0 text-green-500" />
                  <div>
                    <h3 className="text-lg font-semibold">Devenez la personne la plus intéressante</h3>
                    <p className="text-muted-foreground">Maîtrisez les sujets de conversation qui font la différence.</p>
                  </div>
                </li>
              </ul>
            </div>
        </section>


        {/* Section G: Notre Promesse */}
        <section id="promise" className="w-full flex justify-center bg-gray-900 py-16 text-white md:py-24">
          <div className="container text-center px-4 md:px-6">
            <h2 className="font-headline text-3xl font-bold tracking-tight text-accent sm:text-4xl">Plus qu'un résumé, un super-pouvoir.</h2>
            <p className="mx-auto mt-6 max-w-3xl text-lg text-gray-300">
              Chez My Right Info, nous ne vendons pas de l'actu. Nous vous rendons votre temps de cerveau disponible. Nous promettons une neutralité radicale pour que vous puissiez forger VOTRE propre opinion. C'est le droit de savoir, rétabli.
            </p>
          </div>
        </section>

        {/* Section I: Offre */}
        <section className="w-full flex justify-center bg-background py-16 md:py-24">
          <div className="container text-center px-4 md:px-6">
             <h2 className="font-headline text-3xl font-bold tracking-tight sm:text-4xl">Rejoignez l'élite informée.</h2>
             <div className="mx-auto mt-8 grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-2">
                <Card className="border-red-500/50 bg-red-500/5">
                  <CardHeader>
                    <CardTitle className="text-red-700">Lecture Classique</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-left">
                    <p className="flex items-center gap-2"><ThumbsDown className="text-red-500"/> <strong>3h/jour</strong> pour rester à jour.</p>
                    <p className="flex items-center gap-2"><ThumbsDown className="text-red-500"/> Biaisé, anxiogène, bruyant.</p>
                    <p className="flex items-center gap-2"><ThumbsDown className="text-red-500"/> Risque de désinformation élevé.</p>
                  </CardContent>
                </Card>
                 <Card className="border-green-500/50 bg-green-500/5">
                  <CardHeader>
                    <CardTitle className="text-green-700">My Right Info</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 text-left">
                    <p className="flex items-center gap-2"><CheckCircle className="text-green-500"/> <strong>5 min/jour</strong> pour tout maîtriser.</p>
                    <p className="flex items-center gap-2"><CheckCircle className="text-green-500"/> Factuel, serein, essentiel.</p>
                    <p className="flex items-center gap-2"><CheckCircle className="text-green-500"/> Votre propre opinion, éclairée.</p>
                  </CardContent>
                </Card>
             </div>
          </div>
        </section>
        
        {/* Section H: FAQ */}
        <section id="faq" className="w-full flex justify-center bg-white py-16 md:py-24">
          <div className="container mx-auto max-w-2xl px-4 md:px-6">
             <div className="mb-12 text-center">
                <h2 className="font-headline text-3xl font-bold tracking-tight sm:text-4xl">Vos questions, nos réponses transparentes.</h2>
             </div>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="item-1">
                <AccordionTrigger>Comment garantissez-vous que les résumés ne sont pas biaisés politiquement ?</AccordionTrigger>
                <AccordionContent className="leading-relaxed">
                 Notre IA ne "pense" pas, elle synthétise. Contrairement à un éditorialiste humain, notre algorithme croise systématiquement des dizaines de sources aux orientations opposées (gauche, droite, centre, international) pour n'extraire que les faits communs et vérifiables. Nous éliminons les adjectifs émotionnels pour vous livrer l'information brute.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger>Quelles sources votre IA utilise-t-elle pour générer les briefings ?</AccordionTrigger>
                <AccordionContent className="leading-relaxed">
                  Nous scannons en temps réel les flux des agences de presse majeures (AFP, AP, Bloomberg...) ainsi que les grands titres de la presse internationale reconnus pour leur fiabilité. Nous excluons les blogs d'opinion non vérifiés et les réseaux sociaux pour garantir une base saine.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-3">
                <AccordionTrigger>Pourquoi utiliser My Right Info plutôt que de lire les gros titres ?</AccordionTrigger>
                <AccordionContent className="leading-relaxed">
                  Les gros titres vous donnent le "Quoi", mais rarement le "Pourquoi". Lire 10 articles pour comprendre un conflit prend 45 minutes. My Right Info le fait en 30 secondes. Nous condensons le contexte, les enjeux et les faits en un format lisible en un coup d'œil. C'est un gain de temps massif.
                </AccordionContent>
              </AccordionItem>
               <AccordionItem value="item-4">
                <AccordionTrigger>L'IA peut-elle commettre des erreurs ou "halluciner" ?</AccordionTrigger>
                <AccordionContent className="leading-relaxed">
                 Aucune technologie n'est infaillible. Cependant, notre système utilise une architecture de "Vérification Croisée" (Fact-Checking Loop). Si une information n'est présente que dans une seule source, elle est mise de côté. Nous privilégions la fiabilité à la spéculation.
                </AccordionContent>
              </AccordionItem>
               <AccordionItem value="item-5">
                <AccordionTrigger>Le service est-il payant ?</AccordionTrigger>
                <AccordionContent className="leading-relaxed">
                 La génération du "Briefing Quotidien Essentiel" est 100% gratuite et le restera toujours. Nous proposerons prochainement des fonctionnalités Premium pour des analyses sectorielles poussées ou des archives illimitées, mais l'accès à l'information citoyenne reste libre.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </section>


        {/* Section J: CTA FINAL */}
        <section id="cta-final" className="w-full flex justify-center text-center bg-background py-20 md:py-32">
          <div className="container px-4 md:px-6">
            <Button size="lg" asChild className="h-14 px-10 text-lg pulse-button">
              <Link href="/briefing" prefetch={false}>
                JE VEUX MON BRIEF MAINTENANT
              </Link>
            </Button>
            <p className="mt-4 text-xs text-muted-foreground">Pas de carte requise, c'est 100% gratuit frérot.</p>
          </div>
        </section>

      </main>

      <footer className="w-full border-t bg-background">
        <div className="container flex flex-col items-center justify-between gap-4 py-6 sm:flex-row px-4 md:px-6">
            <p className="text-sm text-muted-foreground">&copy; 2025 My Right Info. Tous droits réservés au Boss.</p>
            <nav className="flex gap-4">
              <Link href="#" className="text-sm hover:underline" prefetch={false}>
                Mentions Légales
              </Link>
              <Link href="#" className="text-sm hover:underline" prefetch={false}>
                Confidentialité
              </Link>
            </nav>
        </div>
      </footer>

      <style jsx global>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animation-delay-2000 {
          animation-delay: -2s;
        }
      `}</style>
    </div>
  );
}
